import { SdkApiClient } from '../api/client.js';
import { HEARTBEAT_INTERVAL_MS, SDK_VERSION, assertNonEmpty, createUuid, normalizeError, normalizeBaseUrl, noopLogger, readNavigatorMetadata, sanitizePrimitiveProperties, } from '../shared.js';
const STATE_KEY = 'runtime';
export class BrowserAudienceRuntime {
    constructor(params) {
        this.inFlightForeground = null;
        this.apiClient = new SdkApiClient(params.publishableKey);
        this.baseUrl = normalizeBaseUrl(params.baseUrl);
        this.logger = params.logger ?? noopLogger;
        this.store = params.store;
        this.state = this.readState();
        this.visibilityHandler = () => {
            if (document.visibilityState === 'visible') {
                void this.onForeground().catch((error) => {
                    this.logger.error('Failed to refresh PillowSDK session on foreground', {
                        baseUrl: this.baseUrl,
                        error: normalizeError(error, 'Failed to refresh session').message,
                    });
                });
            }
        };
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.visibilityHandler);
            if (document.visibilityState === 'visible') {
                void this.onForeground().catch((error) => {
                    this.logger.error('Failed to start PillowSDK session on initialization', {
                        baseUrl: this.baseUrl,
                        error: normalizeError(error, 'Failed to start session').message,
                    });
                });
            }
        }
    }
    async onForeground() {
        if (this.inFlightForeground) {
            return this.inFlightForeground;
        }
        this.inFlightForeground = this.ensureForeground().finally(() => {
            this.inFlightForeground = null;
        });
        return this.inFlightForeground;
    }
    currentSessionToken() {
        return this.state.sessionToken;
    }
    currentApiBaseUrl() {
        return this.state.apiBaseUrl;
    }
    currentStudyBaseUrl() {
        return this.state.studyBaseUrl;
    }
    async setExternalId(externalId) {
        this.state.externalId = assertNonEmpty(externalId, 'externalId');
        this.persist();
        const sessionToken = await this.onForeground();
        await this.identifyWithSession(sessionToken, this.state.externalId);
        this.logger.info('PillowSDK external ID synced', {
            externalId: this.state.externalId,
        });
    }
    async setUserProperty(key, value) {
        const trimmedKey = assertNonEmpty(key, 'key');
        this.state.userProperties = sanitizePrimitiveProperties({
            ...this.state.userProperties,
            [trimmedKey]: value,
        });
        this.persist();
        await this.syncUserProperties();
        this.logger.info('PillowSDK user property synced', {
            key: trimmedKey,
            value,
        });
    }
    async clearUserProperty(key) {
        const trimmedKey = assertNonEmpty(key, 'key');
        if (!(trimmedKey in this.state.userProperties)) {
            return;
        }
        const next = { ...this.state.userProperties };
        delete next[trimmedKey];
        this.state.userProperties = next;
        this.persist();
        await this.syncUserProperties();
        this.logger.info('PillowSDK user property cleared', {
            key: trimmedKey,
        });
    }
    async clearAllProperties() {
        this.state.userProperties = {};
        this.persist();
        await this.syncUserProperties();
        this.logger.info('PillowSDK user properties cleared');
    }
    async reset() {
        const nextAnonymousId = createUuid();
        const nextClientSessionId = createUuid();
        this.state.externalId = null;
        this.state.userProperties = {};
        if (!this.state.sessionToken) {
            this.state.anonymousId = nextAnonymousId;
            this.state.clientSessionId = nextClientSessionId;
            this.state.lastHeartbeatAt = 0;
            this.persist();
            await this.onForeground();
            return;
        }
        const metadata = readNavigatorMetadata();
        const response = await this.apiClient.reset(this.state.apiBaseUrl, {
            session_token: this.state.sessionToken,
            installation_id: this.state.installationId,
            anonymous_id: this.state.anonymousId,
            client_session_id: this.state.clientSessionId,
            new_anonymous_id: nextAnonymousId,
            new_client_session_id: nextClientSessionId,
            platform: 'web',
            sdk_version: SDK_VERSION,
            ...metadata,
        });
        this.state.anonymousId = nextAnonymousId;
        this.state.clientSessionId = nextClientSessionId;
        this.applySessionResponse(response.session_token, response.token_expires_at, response.endpoints);
    }
    dispose() {
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
        }
    }
    async ensureForeground() {
        if (!this.state.sessionToken) {
            return this.bootstrap();
        }
        if (Date.now() - this.state.lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) {
            return this.state.sessionToken;
        }
        try {
            const metadata = readNavigatorMetadata();
            const response = await this.apiClient.heartbeat(this.state.apiBaseUrl, {
                session_token: this.state.sessionToken,
                ...metadata,
            });
            if (!response.session_token) {
                return this.bootstrap();
            }
            this.applySessionResponse(response.session_token, response.token_expires_at, response.endpoints);
            return response.session_token;
        }
        catch {
            this.state.sessionToken = null;
            this.state.lastHeartbeatAt = 0;
            this.persist();
            return this.bootstrap();
        }
    }
    async bootstrap() {
        const metadata = readNavigatorMetadata();
        const response = await this.apiClient.bootstrap(this.baseUrl, {
            installation_id: this.state.installationId,
            anonymous_id: this.state.anonymousId,
            client_session_id: this.state.clientSessionId,
            platform: 'web',
            sdk_version: SDK_VERSION,
            ...metadata,
        });
        this.applySessionResponse(response.session_token, response.token_expires_at, response.endpoints);
        this.logger.info('PillowSDK audience session started', {
            apiBaseUrl: response.endpoints.api_base_url,
            identified: response.identified,
        });
        if (this.state.externalId) {
            await this.identifyWithSession(response.session_token, this.state.externalId);
        }
        else if (Object.keys(this.state.userProperties).length > 0) {
            await this.syncUserPropertiesWithSession(response.session_token);
        }
        if (!this.state.sessionToken) {
            throw new Error('PillowSDK could not start a session');
        }
        return this.state.sessionToken;
    }
    async syncUserProperties() {
        this.state.userProperties = sanitizePrimitiveProperties(this.state.userProperties);
        this.persist();
        const sessionToken = await this.onForeground();
        await this.syncUserPropertiesWithSession(sessionToken);
    }
    async syncUserPropertiesWithSession(sessionToken) {
        await this.apiClient.setUserProperties(this.state.apiBaseUrl, sessionToken, this.state.userProperties);
    }
    async identifyWithSession(sessionToken, externalId) {
        const response = await this.apiClient.identify(this.state.apiBaseUrl, sessionToken, externalId);
        this.applySessionResponse(response.session_token, response.token_expires_at, response.endpoints);
    }
    applySessionResponse(sessionToken, tokenExpiresAt, endpoints) {
        this.state.sessionToken = sessionToken;
        this.state.tokenExpiresAt = tokenExpiresAt;
        this.state.seedBaseUrl = this.baseUrl;
        this.state.apiBaseUrl = normalizeBaseUrl(endpoints.api_base_url);
        this.state.studyBaseUrl = endpoints.study_base_url;
        this.state.lastHeartbeatAt = Date.now();
        this.persist();
    }
    readState() {
        const stored = this.store.readJson(STATE_KEY);
        if (stored) {
            const configuredBaseUrl = this.baseUrl;
            const storedSeedBaseUrl = normalizeBaseUrl(stored.seedBaseUrl ?? stored.apiBaseUrl);
            const storedApiBaseUrl = normalizeBaseUrl(stored.apiBaseUrl);
            const shouldResetSession = storedSeedBaseUrl !== configuredBaseUrl;
            return {
                ...stored,
                installationId: assertNonEmpty(stored.installationId, 'installationId'),
                anonymousId: assertNonEmpty(stored.anonymousId, 'anonymousId'),
                clientSessionId: assertNonEmpty(stored.clientSessionId, 'clientSessionId'),
                seedBaseUrl: configuredBaseUrl,
                sessionToken: shouldResetSession ? null : stored.sessionToken,
                tokenExpiresAt: shouldResetSession ? null : stored.tokenExpiresAt,
                apiBaseUrl: shouldResetSession ? configuredBaseUrl : storedApiBaseUrl,
                studyBaseUrl: shouldResetSession ? null : stored.studyBaseUrl,
                userProperties: sanitizePrimitiveProperties(stored.userProperties ?? {}),
                lastHeartbeatAt: shouldResetSession ? 0 : stored.lastHeartbeatAt,
            };
        }
        return {
            installationId: createUuid(),
            anonymousId: createUuid(),
            clientSessionId: createUuid(),
            sessionToken: null,
            tokenExpiresAt: null,
            seedBaseUrl: this.baseUrl,
            apiBaseUrl: this.baseUrl,
            studyBaseUrl: null,
            externalId: null,
            userProperties: {},
            lastHeartbeatAt: 0,
        };
    }
    persist() {
        this.store.writeJson(STATE_KEY, this.state);
    }
}
//# sourceMappingURL=runtime.js.map